/**
 * Dev-only logger. All output is stripped in production builds.
 * Vite replaces `import.meta.env.DEV` with `false` at build time,
 * so dead-code elimination removes every logger call from the bundle.
 *
 * Usage:
 *   logger.log('NavBar', 'Complete Profile logic', { isProfileComplete })
 *   logger.warn('LoginModal', 'Token refresh failed (non-critical)', error)
 *   logger.error('TutorRegistration', 'Submit failed', error)
 *   logger.debug('AuthProvider', 'Token state', { token, isExpired })
 *   logger.group('TutorRegistration', 'handleSubmit', () => {
 *     logger.log('TutorRegistration', 'Payload', payload)
 *     logger.log('TutorRegistration', 'Response', response)
 *   })
 *   const done = logger.time('TutorRegistration', 'API call')
 *   await updateTutorProfile(payload)
 *   done()
 *
 * Timestamp Display:
 *   Pass true as last parameter to include timestamp (default: false):
 *   logger.log('NavBar', 'message', value, true)
 */

const isDev = import.meta.env.DEV;

const timestamp = (): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
};

const prefix = (component: string, showTimestamp: boolean = false): string => 
  showTimestamp ? `${timestamp()} [${component}]` : `[${component}]`;

export const logger = {
  log: (component: string, message: string, value?: unknown, showTimestamp: boolean = false): void => {
    if (!isDev) return;
    value !== undefined
      ? console.log(`${prefix(component, showTimestamp)} ${message}`, value)
      : console.log(`${prefix(component, showTimestamp)} ${message}`);
  },

  debug: (component: string, message: string, value?: unknown, showTimestamp: boolean = false): void => {
    if (!isDev) return;
    value !== undefined
      ? console.debug(`${prefix(component, showTimestamp)} ${message}`, value)
      : console.debug(`${prefix(component, showTimestamp)} ${message}`);
  },

  warn: (component: string, message: string, value?: unknown, showTimestamp: boolean = false): void => {
    if (!isDev) return;
    value !== undefined
      ? console.warn(`${prefix(component, showTimestamp)} ${message}`, value)
      : console.warn(`${prefix(component, showTimestamp)} ${message}`);
  },

  error: (component: string, message: string, value?: unknown, showTimestamp: boolean = false): void => {
    if (!isDev) return;
    value !== undefined
      ? console.error(`${prefix(component, showTimestamp)} ${message}`, value)
      : console.error(`${prefix(component, showTimestamp)} ${message}`);
  },

  /**
   * Groups related logs under a collapsible label in devtools.
   * The callback runs synchronously inside the group.
   */
  group: (component: string, label: string, fn: () => void, showTimestamp: boolean = false): void => {
    if (!isDev) return;
    console.groupCollapsed(`${prefix(component, showTimestamp)} ${label}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },

  /**
   * Starts a timer. Call the returned function to stop and print elapsed ms.
   */
  time: (component: string, label: string, showTimestamp: boolean = false): (() => void) => {
    if (!isDev) return () => {};
    const start = performance.now();
    return () => {
      const ms = (performance.now() - start).toFixed(1);
      console.log(`${prefix(component, showTimestamp)} ${label} — ${ms}ms`);
    };
  },
};
